/**
 * Patient-name redaction for the risk register export — H6 of `tasks/risk-management.md`.
 *
 * A PURE MODULE ON PURPOSE. §6.H6: this is a privacy control, not a formatting detail on a PDF
 * service. It has to be testable in isolation (`redact-name.spec.ts`, H7/K8) and reusable if the
 * register is ever shown on screen redacted. Nothing here touches Angular, jsPDF or the DOM.
 *
 * ── WHAT THIS IS AND IS NOT ──────────────────────────────────────────────────────────────
 *
 * This is PSEUDONYMISATION, not anonymisation. A register row still carries a hospital, a ward, a
 * date and a patient number, and is re-identifiable BY DESIGN — the doctor may need to trace a
 * case when the hospital asks about it. §6.H says so explicitly and instructs that the UI toggle
 * be labelled "Redact patient names" and NEVER "Anonymise". Under POPIA the exported document is
 * still personal information; the masking reduces casual legibility, it does not remove the
 * practice's obligations. Do not soften that wording anywhere that calls this function.
 *
 * ── THE RULES (§6.H.1, implemented verbatim) ─────────────────────────────────────────────
 *
 *   | Input           | Output          | Rule                                                 |
 *   |-----------------|-----------------|------------------------------------------------------|
 *   | `John`          | `J**n`          | len >= 3 -> first + '*' x (len - 2) + last            |
 *   | `Ann`           | `A*n`           | len >= 3, shortest normal case                        |
 *   | `Jo`            | `J*`            | len <= 2 -> first + a SINGLE '*'                      |
 *   | `J`             | `J*`            | same floor                                            |
 *   | `Van der Merwe` | `V***********e` | the WHOLE field is one string; spaces masked too      |
 *   | `Mary-Anne`     | `M*******e`     | same; hyphens masked too                              |
 *   | `` / null       | `—`             | no name, no row content invented                      |
 */

/** What a name-shaped column shows when there is no name. An em dash, not an empty cell. */
export const NO_NAME = '—';

/**
 * Masks one name field.
 *
 * ── THE len <= 2 FLOOR IS NOT OPTIONAL ───────────────────────────────────────────────────
 *
 * The general rule keeps the first and last character. For a two-letter name those ARE the whole
 * name: `Jo` -> `Jo` is not a redaction, it is a no-op that looks like one. So anything of length
 * 1 or 2 collapses to `first + '*'`. This is the single most breakable line in the file, which is
 * why the spec asserts it directly and K8 mutation-tests it: a redaction helper that silently
 * stops redacting still returns a plausible-looking string.
 *
 * ── ONE RUN, NOT ONE RUN PER WORD ────────────────────────────────────────────────────────
 *
 * The field is masked as a SINGLE string, so spaces and hyphens become `*` like any other
 * character. Masking token by token (`V*n d*r M****e`) preserves word count, word lengths and the
 * short connecting words, which together identify a surname far more narrowly than the masked
 * string suggests. §6.H.1 chose readability loss over that leak; do not "improve" this by
 * splitting on whitespace.
 *
 * ── KNOWN AND ACCEPTED ───────────────────────────────────────────────────────────────────
 *
 * The `*` run reveals name LENGTH. A fixed-width mask would hide it, at the cost of the doctor no
 * longer recognising their own patients on the page — which is the entire reason the export
 * redacts rather than omits. Length alone, without the middle letters, is weak.
 *
 * Input is normalised by trimming only. A field that is whitespace-only carries no name and is
 * treated as absent, rather than being masked into a `*` run that implies a name exists.
 */
export function redactName(value: unknown): string {
  if (value === null || value === undefined) return NO_NAME;

  const text = typeof value === 'string' ? value : String(value);
  const trimmed = text.trim();

  if (trimmed.length === 0) return NO_NAME;

  // THE FLOOR. See the header — removing this branch is a privacy leak, not a simplification.
  if (trimmed.length <= 2) return `${trimmed.charAt(0)}*`;

  const first = trimmed.charAt(0);
  const last = trimmed.charAt(trimmed.length - 1);
  return `${first}${'*'.repeat(trimmed.length - 2)}${last}`;
}

/**
 * H8 — DOES THE EXPORT REDACT?
 *
 * The default must hold when the toggle has NEVER BEEN TOUCHED. `undefined` is the state of an
 * options object that simply did not mention redaction, and a plain `if (options.redactNames)`
 * would read that as false and print every patient's full name onto a document leaving the
 * practice on letterhead. So the question is inverted: redaction is ON unless something explicitly
 * turned it OFF.
 *
 * ONLY a literal boolean `false` opts out. `undefined`, `null`, `0`, `''` and every other falsy
 * value mean "not answered", and an unanswered privacy question resolves to the safe side. That
 * asymmetry is the whole point of this function existing rather than being an inline `!==`.
 */
export function shouldRedactNames(optIn: unknown): boolean {
  return optIn !== false;
}

/**
 * The Patient column of the register (H2), for a row whose patient may be NULL.
 *
 * `PatientRiskItem.patientId` IS NULLABLE (§5c) and most facility issues name no patient at all —
 * "pharmacy had no ceftriaxone" is about a pharmacy. That is the NORMAL case here, not an error
 * state, so a missing patient renders as `—` and never as `undefined undefined`.
 *
 * `name` and `surname` are masked SEPARATELY, per §6.H.1's "applied per field". The surname leads,
 * matching every other patient list in the app.
 */
export function redactedPatientLabel(patient: any, redact: unknown = true): string {
  if (!patient) return NO_NAME;

  const surname = patient.surname;
  const name = patient.name;

  if (!shouldRedactNames(redact)) {
    const full = `${String(surname ?? '').trim()}${surname && name ? ', ' : ''}${String(name ?? '').trim()}`.trim();
    return full.length ? full : NO_NAME;
  }

  const hasSurname = String(surname ?? '').trim().length > 0;
  const hasName = String(name ?? '').trim().length > 0;

  if (!hasSurname && !hasName) return NO_NAME;
  if (hasSurname && hasName) return `${redactName(surname)}, ${redactName(name)}`;
  return hasSurname ? redactName(surname) : redactName(name);
}
